import PersonIcon from '@mui/icons-material/Person';
import type { IEntityLogEntry } from '@pulse/shared';
import { Box, Button, ICON_SIZE, LabelWithIcon, MarkdownField, Paper, Stack, Typography } from '@pulse/ui';
import { useState } from 'react';

import { formatDdMmYyyyHm } from '../lib/date.util';

// Generic log-entry UI (§10b Risk log, §12b Escalation log — "same pattern as Risk log") —
// presentational only, same decoupled-from-API-hooks pattern as AttachmentsPanel.
interface LogEntriesPanelProps {
  entries: IEntityLogEntry[];
  isLoading?: boolean;
  onAdd: (note: string) => void;
  isAdding?: boolean;
  disabled?: boolean;
}

// Matches CreateEntityLogDto's @MaxLength(1000) on `note`
// (apps/api/src/modules/presales/dto/create-entity-log.dto.ts).
const NOTE_MAX = 1000;

function overLimitHelperText(value: string, max: number, overLimit: boolean): string {
  return overLimit ? `${value.length}/${max} — ${value.length - max} characters over the limit` : `${value.length}/${max}`;
}

export function LogEntriesPanel({ entries, isLoading, onAdd, isAdding, disabled }: LogEntriesPanelProps) {
  const [note, setNote] = useState('');
  const overLimit = note.length > NOTE_MAX;

  function handleAdd() {
    onAdd(note.trim());
    setNote('');
  }

  return (
    <Stack spacing={1.5}>
      {!disabled && (
        <Stack spacing={1}>
          <MarkdownField
            label=""
            value={note}
            onChange={setNote}
            rows={2}
            error={overLimit}
            helperText={overLimitHelperText(note, NOTE_MAX, overLimit)}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={note.trim().length === 0 || overLimit || isAdding}
            >
              {isAdding ? 'Adding…' : 'Add Log Entry'}
            </Button>
          </Stack>
        </Stack>
      )}

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading log…</Typography>
      ) : entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No log entries yet.</Typography>
      ) : (
        entries.map((entry) => (
          <Paper key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
            <Typography component="div" variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              <LabelWithIcon icon={<PersonIcon sx={{ fontSize: ICON_SIZE.xxxs }} />} label={`${entry.author.name} · ${formatDdMmYyyyHm(entry.createdAt)}`} />
            </Typography>
            <Box>
              <MarkdownField label="" value={entry.note} onChange={() => {}} readOnly />
            </Box>
          </Paper>
        ))
      )}
    </Stack>
  );
}
